// plan-05 S05: animation — 5s hex reveal + 3s line draw, verbatim port from v1

import type { HexGrid } from './geometry'
import type { MaturityResult } from './maturity'
import type { HeatmapSVG } from './render'
import { resetColors, applyMaturityColors, maturityColor, flashColor, clearGroup } from './render'
import { drawConnectionLines, drawMaturityChain, drawPrimeLines, drawGoldenRings } from './lines'

export interface AnimationController {
  enabled: boolean
  cancel(): void
}

export function createAnimationController(): AnimationController {
  return {
    enabled: true,
    cancel() {},
  }
}

export function animateHeatmap(
  grid: HexGrid,
  result: MaturityResult,
  groups: HeatmapSVG,
  ctrl: AnimationController,
): void {
  // cancel any running animation
  ctrl.cancel()

  if (!ctrl.enabled) {
    // instant path
    resetColors(grid, groups)
    applyMaturityColors(grid, result, groups)
    drawConnectionLines(grid, result, groups.linesGroup)
    drawPrimeLines(grid, result.hits, groups.primeLinesGroup)
    drawMaturityChain(grid, result.hits, groups.chainGroup)
    clearGroup(groups.ringGroup)
    drawGoldenRings(grid, result, groups.ringGroup)
    return
  }

  // animated path
  resetColors(grid, groups)

  let cancelled = false
  ctrl.cancel = () => { cancelled = true }

  const { hits, goldenGlow } = result
  const { ordered } = grid

  // build reveal order: maturity ascending, shuffled within level
  const activeHexes = ordered.filter(h => hits[h.seqNum] > 0)
  const byMat = new Map<number, typeof activeHexes>()
  for (const h of activeHexes) {
    const v = hits[h.seqNum]
    if (!byMat.has(v)) byMat.set(v, [])
    byMat.get(v)!.push(h)
  }
  const levels = [...byMat.keys()].sort((a, b) => a - b)

  // fisher-yates shuffle
  function shuffle<T>(arr: T[]): T[] {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
  }

  const revealOrder = levels.flatMap(v => shuffle([...byMat.get(v)!]))

  const HEX_PHASE_MS = 5000
  const LINE_PHASE_MS = 3000
  const hexInterval = HEX_PHASE_MS / Math.max(revealOrder.length, 1)

  // phase 1: reveal hexes
  for (let idx = 0; idx < revealOrder.length; idx++) {
    const delay = idx * hexInterval
    const h = revealOrder[idx]
    setTimeout(() => {
      if (cancelled) return
      const i = h.seqNum
      const { fill, stroke } = maturityColor(hits[i])
      const flash = flashColor(hits[i])

      // brief flash
      h.poly!.setAttribute('fill', flash.fill)
      h.poly!.setAttribute('stroke', flash.stroke)
      ;(h.poly!.style as CSSStyleDeclaration).transition = 'fill 0.25s ease, stroke 0.25s ease'

      setTimeout(() => {
        if (cancelled) return
        h.poly!.setAttribute('fill', fill)
        h.poly!.setAttribute('stroke', stroke)
        if (goldenGlow.has(h.seqNum)) {
          h.poly!.setAttribute('filter', 'url(#vt-golden-glow)')
        }
      }, 220)
    }, delay)
  }

  // phase 2: draw lines after hex phase
  setTimeout(() => {
    if (cancelled) return

    // draw lines into DOM invisibly, then animate stroke-dashoffset
    const lineEls: SVGElement[] = []

    // monkey-patch appendChild to intercept line elements for pre-hiding
    const _origAppend = SVGGElement.prototype.appendChild
    const _origInsert = SVGGElement.prototype.insertBefore

    function interceptAppend(el: Node): Node {
      if (el instanceof SVGElement && (el.tagName === 'line' || el.tagName === 'polyline' || el.tagName === 'path')) {
        primeLineForAnim(el)
        lineEls.push(el)
      }
      return el
    }

    SVGGElement.prototype.appendChild = function<T extends Node>(this: SVGGElement, el: T): T {
      interceptAppend(el)
      return _origAppend.call(this, el) as T
    }
    SVGGElement.prototype.insertBefore = function<T extends Node>(this: SVGGElement, el: T, ref: Node | null): T {
      interceptAppend(el)
      return _origInsert.call(this, el, ref) as T
    }

    drawConnectionLines(grid, result, groups.linesGroup)
    drawPrimeLines(grid, hits, groups.primeLinesGroup)
    drawMaturityChain(grid, hits, groups.chainGroup)
    clearGroup(groups.ringGroup)
    drawGoldenRings(grid, result, groups.ringGroup)

    // restore
    SVGGElement.prototype.appendChild = _origAppend
    SVGGElement.prototype.insertBefore = _origInsert

    // stagger line draws
    const LINE_DRAW_MS = 600
    const lineInterval = lineEls.length > 1
      ? (LINE_PHASE_MS - LINE_DRAW_MS) / (lineEls.length - 1)
      : 0

    for (let idx = 0; idx < lineEls.length; idx++) {
      setTimeout(() => {
        if (cancelled) return
        animateLine(lineEls[idx], LINE_DRAW_MS)
      }, idx * lineInterval)
    }
  }, HEX_PHASE_MS + 50)
}

function primeLineForAnim(el: SVGElement): void {
  el.setAttribute('stroke-dasharray', '99999')
  el.setAttribute('stroke-dashoffset', '99999')
}

function animateLine(el: SVGElement, duration: number): void {
  const len = (() => {
    try {
      const l = (el as SVGGeometryElement).getTotalLength()
      if (l > 0) return l
    } catch { /* not a path */ }
    const x1 = parseFloat(el.getAttribute('x1') ?? '0')
    const y1 = parseFloat(el.getAttribute('y1') ?? '0')
    const x2 = parseFloat(el.getAttribute('x2') ?? '0')
    const y2 = parseFloat(el.getAttribute('y2') ?? '0')
    return Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2) || 100
  })()

  el.setAttribute('stroke-dasharray', String(len))
  el.setAttribute('stroke-dashoffset', String(len))

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      el.style.transition = `stroke-dashoffset ${duration}ms ease-in-out`
      el.setAttribute('stroke-dashoffset', '0')
    })
  })
}
